/**
 * Центральный API-клиент SCALPEX Mini App.
 *
 * Все запросы к бэкенду идут через этот файл.
 * Авторизация: в каждый запрос добавляется заголовок
 *   Authorization: tma <initData>
 * где initData — подписанные Telegram данные пользователя.
 * Бэкенд (FastAPI) проверяет HMAC-подпись через BOT_TOKEN и
 * идентифицирует пользователя без паролей.
 *
 * Переменная окружения VITE_API_URL задаёт адрес бэкенда.
 * В разработке Vite проксирует /api на localhost:8000 (см. vite.config.js).
 */

const BASE_URL = import.meta.env.VITE_API_URL ?? '/api'

/** Получить initData от Telegram WebApp */
function getInitData() {
  return window.Telegram?.WebApp?.initData ?? ''
}

// ─── Режим юзера (п.1-7 task.txt) ──────────────────────────────────────────
// Админ смотрит приложение глазами нового юзера: при включённом режиме фронт
// добавляет к КАЖДОМУ запросу заголовок X-Impersonate: 1. Бэкенд подменяет
// userId админа на тестового юзера (кроме /api/admin/* — админка остаётся
// админкой). Флаг живёт в памяти + localStorage, чтобы переживал перезагрузку.
let impersonate = false

export function setImpersonate(v) {
  impersonate = !!v
  try {
    if (impersonate) localStorage.setItem('scalpex-view-mode', '1')
    else localStorage.removeItem('scalpex-view-mode')
  } catch { /* приватный режим — не критично */ }
}
export const isImpersonating = () => impersonate

// ─── Диагностика сбоев загрузки (п.3 task.txt) ─────────────────────────────
// «Мини-апп иногда у некоторых пользователей не открывается» — без данных с
// устройства причину не узнать. Здесь короткая запись уходит на бэкенд
// (POST /client-log → TG-BOT/logs/client.log). Правила:
//  • «выстрелил и забыл» — никогда не бросает и ничего не возвращает;
//  • initData НЕ требуется (иначе лог не запишется как раз при сбое авторизации);
//  • троттлинг на клиенте (не чаще 1 записи в 5 с и не больше 15 за сессию),
//    чтобы цикл ошибок не залил сервер и не тормозил загрузку.
let lastLogAt = 0
let logCount = 0

export function sendClientLog(payload = {}) {
  try {
    const now = Date.now()
    if (now - lastLogAt < 5000) return
    if (logCount >= 15) return
    lastLogAt = now
    logCount += 1

    const body = JSON.stringify({
      stage: String(payload.stage ?? 'unknown').slice(0, 60),
      message: String(payload.message ?? '').slice(0, 500),
      platform: String(payload.platform ?? '').slice(0, 40),
      version: String(payload.version ?? '').slice(0, 40),
      appVersion: String(payload.appVersion ?? '').slice(0, 20),
      hasInitData: !!payload.hasInitData,
      ua: String(payload.ua ?? navigator.userAgent ?? '').slice(0, 300),
    })

    // fetch с keepalive: запись успевает уйти, даже если страница уже уходит
    // на перезагрузку. Ошибки глушим — диагностика не влияет на приложение.
    fetch(`${BASE_URL}/client-log`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {})
  } catch { /* диагностика не должна ломать загрузку */ }
}

/**
 * Базовая функция запроса.
 * Автоматически добавляет заголовок авторизации и обрабатывает ошибки.
 */
async function request(path, options = {}) {
  const initData = getInitData()

  const headers = {
    'Content-Type': 'application/json',
    ...(initData ? { 'X-Telegram-Init-Data': initData } : {}),
    // Режим юзера: включаем имперсонацию для всех путей КРОМЕ админки —
    // иначе админ не сможет выйти из режима во вкладке «Админ».
    ...(impersonate && !String(path).startsWith('/admin') ? { 'X-Impersonate': '1' } : {}),
    ...(options.headers ?? {}),
  }

  const res = await fetch(`${BASE_URL}${path}`, { ...options, headers })

  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }))
    const err = new Error(body.detail ?? `HTTP ${res.status}`)
    if (body.retryAfter) err.retryAfter = body.retryAfter
    throw err
  }

  // 204 No Content — нет тела ответа
  if (res.status === 204) return null
  return res.json()
}

// ─── Пользователь / Авторизация ───────────────────────────────────────────
//
// Вызывается один раз при старте Mini App.
// Бэкенд проверяет initData, создаёт или находит пользователя в БД
// и возвращает его профиль: id, имя, дата подписки.
//
// GET /api/users/me
// Ответ: { id, tg_user_id, username, subscription_end }
//
export const getMe = () => request('/users/me')

// GET /api/public/bot-info — реальное имя бота (t.me-ссылки строит генератор
// UTM и веб-лендинг). Публичный роут: авторизация не нужна (лендинг открывается
// вне Telegram). Фолбэк на зашитую константу держит сам utm.js.
export const getPublicBotInfo = () => request('/public/bot-info')

// ─── Боты ─────────────────────────────────────────────────────────────────
//
// Каждый бот хранится в БД привязанным к пользователю.
// Алгоритм Python читает из БД список активных ботов
// и торгует по их настройкам.

/** Получить все боты текущего пользователя */
// GET /api/bots
// Ответ: [{ id, name, type, exchange, status, profit, closedTrades,
//            commission, openPositions, createdAt, workTime, algoParams }]
export const getBots = () => request('/bots')

/** Создать нового бота */
// POST /api/bots
// Тело: { name, type, exchange, algoParams: { leverage, tradingMode,
//          orders, globalVolume, tp, sl, blacklist } }
// Ответ: созданный бот (с id, status='active', profit=0 и т.д.)
export const createBot = (data) =>
  request('/bots', { method: 'POST', body: JSON.stringify(data) })

/** Обновить настройки бота (algoParams, name) */
// PATCH /api/bots/:botId
// Тело: любые поля бота для обновления
// Ответ: обновлённый бот
export const updateBot = (botId, data) =>
  request(`/bots/${botId}`, { method: 'PATCH', body: JSON.stringify(data) })

/** Удалить бота */
// DELETE /api/bots/:botId
// Ответ: 204 No Content
export const deleteBot = (botId) =>
  request(`/bots/${botId}`, { method: 'DELETE' })

/** Запустить бота (status = 'active') */
// POST /api/bots/:botId/start
// Ответ: { status: 'active' }
export const startBot = (botId) =>
  request(`/bots/${botId}/start`, { method: 'POST' })

/** Остановить бота (status = 'paused') */
// POST /api/bots/:botId/stop
// Ответ: { status: 'paused' }
export const stopBot = (botId) =>
  request(`/bots/${botId}/stop`, { method: 'POST' })

// ─── Статистика / Дашборд ─────────────────────────────────────────────────
//
// Данные для графиков и карточек на вкладке "Главная".
// Алгоритм Python после каждой сделки обновляет таблицу stats в БД.

/** Получить сводную статистику по всем ботам пользователя */
// GET /api/stats?period=daily|weekly|monthly|total
// Ответ: { totalProfit, todayProfit, yesterdayProfit, winRate,
//          totalTrades, avgProfit, chartData: [...] }
export const getStats = (period = 'daily') =>
  request(`/stats?period=${period}`)

// ─── Закрытые позиции (Дневник) ───────────────────────────────────────────
//
// Каждая закрытая сделка записывается алгоритмом в БД.
// Дневник читает этот список и отображает его.

/** Получить закрытые позиции */
// GET /api/positions?botId=...&from=YYYY-MM-DD&to=YYYY-MM-DD&limit=50
// Ответ: [{ id, pair, side, pnl, entry, exit, duration, closedAt,
//            botName, size, commission, leverage, roi, roiMargin }]
export const getPositions = (params = {}) => {
  const qs = new URLSearchParams(params).toString()
  return request(`/positions${qs ? '?' + qs : ''}`)
}

// ─── Живой лидерборд ────────────────────────────────────────────────────────
//
// Список виртуальных трейдеров и их недельный PnL/ROI. Данные формирует
// leaderboardSim.js на сервере по ставкам фандинга Bybit.

/** Получить топ трейдеров лидерборда */
// GET /api/leaderboard
// Ответ: [{ trader, totalPnl, roiWeek, days, deposit? }] (ранг — по индексу)
export const getLeaderboard = () => request('/leaderboard')

/** Живая лента лидерборда (п.11, п.23-25, п.28 task.txt) */
// GET /api/leaderboard/live
// Ответ: { today: { pnl, trades }, recent: [{ trader, symbol, pnl, deposit, ts }] }
// today  — сколько виртуальные юзеры лидерборда заработали сегодня (FOMO)
// recent — последние сделки для ленты (имя уже замаскировано, uid вырезан)
export const getLeaderboardLive = () => request('/leaderboard/live')

// ─── API-ключи бирж ───────────────────────────────────────────────────────
//
// Ключи хранятся на бэкенде зашифрованными.
// Алгоритм Python читает их из БД и использует для торговли через ccxt.
// В Mini App ключи никогда не показываются повторно после сохранения.

/** Получить список подключённых бирж пользователя */
// GET /api/exchanges
// Ответ: [{ exchange, connected, maskedKey }]
export const getExchanges = () => request('/exchanges')

/** Проверить наличие подключённых API ключей у пользователя */
// GET /api/exchanges/has-keys
// Ответ: { hasKeys: true }
export const hasApiKeys = () => request('/exchanges/has-keys')

/** Подключить биржу — сохранить API ключи */
// POST /api/exchanges/connect
// Тело: { exchange: 'bybit', real_key: '...', real_secret: '...', demo_key: '...', demo_secret: '...', passphrase: '...' }
// Ответ: { status: 'success', message: 'API keys saved' }
// passphrase (Bitget, API v2, 2026-09-17, task.txt п.3): обязательна для
// Bitget, остальные биржи её игнорируют (бэкенд примет и сохранит, но
// использовать не будет).
export const connectExchange = (exchange, apiKey, apiSecret, demoKey = '', demoSecret = '', passphrase = '') =>
  request('/exchanges/connect', {
    method: 'POST',
    body: JSON.stringify({
      exchange,
      real_key: apiKey,
      real_secret: apiSecret,
      demo_key: demoKey,
      demo_secret: demoSecret,
      passphrase,
    }),
  })

/** Отключить биржу — удалить API ключи */
// DELETE /api/exchanges/:exchange
// Ответ: 204 No Content
export const disconnectExchange = (exchange) =>
  request(`/exchanges/${exchange}`, { method: 'DELETE' })

/** Получить доступный баланс USDT на бирже */
// GET /api/exchanges/:exchange/balance
// Ответ: { available: 1250.00, currency: 'USDT' }
export const getExchangeBalance = (exchange) =>
  request(`/exchanges/${exchange}/balance`)

// ─── Глобальный конфиг алгоритма ──────────────────────────────────────────
//
// Настройки из вкладки "Боты" -> "Конфигурация".
// Алгоритм читает их перед каждым циклом скрининга.
//
// Хранение на сервере: список пар записывается построчно в файл
// users_DATA/{userId}/BLACKLIST.txt (см. TG-BOT/src/api.js, эндпоинты
// GET/POST /api/config).

/** Получить конфиг пользователя */
// GET /api/config
// Ответ: { blacklistedPairs: string[] }
export const getConfig = () => request('/config')

/** Сохранить конфиг пользователя */
// POST /api/config
// Тело: { blacklistedPairs: string[] }
// Ответ: { ok: true, blacklistedPairs: string[] }
export const saveConfig = (config) =>
  request('/config', { method: 'POST', body: JSON.stringify(config) })

// ─── Подписка ─────────────────────────────────────────────────────────────
//
// Бэкенд хранит дату окончания подписки.
// Mini App показывает её на вкладке "Главная" в профиле пользователя.

/** Получить статус подписки */
// GET /api/subscription
// Ответ: { active: true, expiresAt: '2026-03-28' }
export const getSubscription = () => request('/subscription')

// ─── Подписка: оплата (крипто-приём) ──────────────────────────────────────
//
// Тарифы и сеть хранятся в БД. Платёж создаётся на 20 минут: адрес берётся
// из пула депозитных кошельков, курс монеты фиксируется на момент создания.

/** Получить тарифы и наличие свободных адресов по сетям */
// GET /api/subscription/plans
// Ответ: { plans: [{code,title_ru,type,months,price_usdt,discount}],
//          networks: {TRC20:2,BEP20:1,SOL_USDT:0,TON:3,ETH:0,SOL:2} }
export const getSubscriptionPlans = () => request('/subscription/plans')

/** Telegram Stars: выставить счёт «в один клик» (currency XTR) */
// GET /api/subscription/stars-invoice?plan=<code>
// Ответ: { ok, invoiceLink, planCode, stars, priceUsdt, currency, payload }
// invoiceLink открывается через window.Telegram.WebApp.openInvoice.
export const getSubscriptionStarsInvoice = (planCode) =>
  request(`/subscription/stars-invoice?plan=${encodeURIComponent(planCode)}`)

/** Создать заявку на оплату (выдаёт адрес из пула + фиксирует курс) */
// POST /api/subscription/payment
// Тело: { planCode: 'real_1m', network: 'BEP20' }
// Ответ: { id, address, amountCoin, currency, rate, amountUsdt, planCode,
//          network, planTitle, expiresAt }
export const createPaymentRequest = (planCode, network) =>
  request('/subscription/payment', {
    method: 'POST',
    body: JSON.stringify({ planCode, network }),
  })

/** Статус заявки на оплату */
// GET /api/subscription/payment/:id
// Ответ: { id, status, activated, subscriptionEnd, expiresAt, txHash }
export const getPaymentStatus = (paymentId) =>
  request(`/subscription/payment/${paymentId}`)

/** «Я отправил платёж» — принудительная проверка сети */
// POST /api/subscription/payment/:id/confirm
// Ответ: { id, status, activated, subscriptionEnd, expiresAt, txHash,
//          verified: bool, verifyError: string|null }
export const confirmPaymentRequest = (paymentId) =>
  request(`/subscription/payment/${paymentId}/confirm`, { method: 'POST' })

/** Отменить заявку на оплату (кнопка «Отмена» в окне оплаты) */
// POST /api/subscription/payment/:id/cancel
// Ответ: { ok: true, changed: bool, status: string }
// Заявка помечается failed, адрес мгновенно возвращается в пул.
export const cancelPaymentRequest = (paymentId) =>
  request(`/subscription/payment/${paymentId}/cancel`, { method: 'POST' })

/** Автоотправка обращения в чат поддержки */
// POST /api/support/send
// Тело: { text } — готовое сообщение (выбранный вариант проблемы + контекст).
// Бэкенд пересылает его в чат поддержки через Telegram API.
export const sendSupportMessage = (text) =>
  request('/support/send', { method: 'POST', body: JSON.stringify({ text }) })

/** Очистить дневник (удалить все записи из PNL.txt или PNL_DEMO.txt) */
// POST /api/diary/clear?demo=true|false
// Ответ: { ok: true }
export const clearDiary = (isDemo = false) => request(`/diary/clear?demo=${isDemo}`, { method: 'POST' })

/** Сохранить язык пользователя (ru | en) — синхронизация с ботом */
// POST /api/users/language
// Тело: { language: 'ru' | 'en' }
// Ответ: { ok: true, language }
export const setUserLanguage = (language) =>
  request('/users/language', { method: 'POST', body: JSON.stringify({ language }) })

/** Сохранить настройки уведомлений юзера (бот шлёт их по этим флагам) */
// PATCH /api/users/me/notifications
// Тело: { trades: boolean } | { funding: boolean } | оба сразу
// trades   — «Торговые сигналы»: уведомления о закрытых сделках бота
// funding  — «Уведомления о фандингах»: батч за 5 минут до выплаты (п.8 task.txt)
// Ответ: { ok: true, trades?, funding? }
export const setUserNotifications = ({ trades, funding } = {}) =>
  request('/users/me/notifications', {
    method: 'PATCH',
    body: JSON.stringify({
      ...(trades !== undefined ? { trades } : {}),
      ...(funding !== undefined ? { funding } : {}),
    }),
  })

/** Отметить, что юзер увидел уведомление о смене рекомендуемого режима (task.txt №3) */
// POST /api/users/me/seen-mode-notice
// Тело: { at: <modeChangedAt, мс> } — время смены режима, которую юзер увидел.
// Ответ: { ok: true }
export const setModeNoticeSeen = (at) =>
  request('/users/me/seen-mode-notice', { method: 'POST', body: JSON.stringify({ at }) })

// ─── Реферальная программа (п.4.4 ПЛАН_МАРКЕТИНГА) ────────────────────────
//
// Пригласительная ссылка — классический deep-link бота:
//   https://t.me/<бот>?start=ref_<код>
// Открывший её друг присылает боту /start ref_XXXXXX, и бот привязывает его
// сам (TG-BOT/src/startPayload.js) — ещё до открытия приложения. Если когда-то
// включат главное мини-приложение, сработает и второй путь: start_param на
// фронте → bindReferral. Награда — 10% от ПЕРВОЙ оплаченной подписки друга
// (начисляет сервер, один раз на друга). Выплата — вручную, до 5-го числа
// следующего месяца; статус «выплачено» ставит админ в разделе «Рефералы».

/** Сводка реферального блока в Настройках: код, ссылка, счётчики, «к выплате» */
// GET /api/referral/summary
// Ответ: { code, link, invitedCount, paidFriends, earnedAll, duePrev,
//          duePrevPaid, dueDate, accruingThisMonth } (link может быть null,
//          если у бота нет username — dev без запущенного бота)
export const getReferralSummary = () => request('/referral/summary')

/** Привязать «друга» к рефереру по коду из start_param (ref_XXXXXX) */
// POST /api/referral/bind — тело { code }
// Ответ: { ok:true, referrerId } | { ok:false, error }
// Ошибки (already_bound / already_paid / self / not_found) — НОРМАЛЬНЫЕ отказы,
// фронт вызывает fire-and-forget и молча их игнорирует.
export const bindReferral = (code) =>
  request('/referral/bind', { method: 'POST', body: JSON.stringify({ code }) })

// ─── Инструкция пользователя ──────────────────────────────────────────────
//
// Полная инструкция по использованию бота. Контент живёт в markdown-файле
// на сервере (TG-BOT/INSTRUCTION.md / INSTRUCTION_EN.md) и читается ПРИ КАЖДОМ
// запросе — владелец правит файл напрямую, правки видны сразу без пересборки.
// Скриншоты из markdown отдаёт тот же API (/api/instruction/img/<имя>).

/** Получить инструкцию пользователя на языке приложения */
// GET /api/instruction?lang=ru|en
// Ответ: { content, lang, updatedAt, available: { ru, en } }
export const getInstruction = (lang) =>
  request(`/instruction?lang=${encodeURIComponent(lang)}`)

/** Получить гайд «Как получить ключи на бирже» (кнопка в инструкции, Шаг 2) */
// GET /api/guide/api-keys?lang=ru|en
// Ответ: { content, lang, updatedAt, available: { ru, en } }
// lang=en отдаёт API_KEYS_GUIDE_EN.md; если английского файла нет — фолбэк на RU.
export const getGuideApiKeys = (lang) =>
  request(`/guide/api-keys?lang=${encodeURIComponent(lang || 'ru')}`)

// ─── Конфиг приложения (админка) ─────────────────────────────────────────
//
// Публичный конфиг читают ВСЕ пользователи: включённые биржи, способы оплаты,
// feature-флаги, флаг «Тех. работы». Полный конфиг и его сохранение доступны
// только админу (id в ADMIN_IDS/ADMIN_ID на бэкенде).

/** Получить публичный конфиг приложения (для всех пользователей) */
// GET /api/app-config
// Ответ: { exchanges, payments, features, maintenance }
export const getAppConfig = () => request('/app-config')

/** Получить полный конфиг приложения (только админ) */
// GET /api/admin/config
// Ответ: { exchanges, payments, features, maintenance, prices, notifications }
export const getAdminConfig = () => request('/admin/config')

/** Сохранить конфиг приложения (только админ) — частичный merge */
// POST /api/admin/config
// Тело: любой патч, например { maintenance: true, exchanges: { binance: true } }
// Ответ: итоговый сохранённый конфиг
export const saveAdminConfig = (patch) =>
  request('/admin/config', { method: 'POST', body: JSON.stringify(patch) })

// ─── Админка: Подписки ────────────────────────────────────────────────────
//
// Раздел «Подписки» в админ-панели: поиск пользователя по id, карточка
// подписки, продление/полная отмена, ручная активация тарифа и проверка
// транзакции по хэшу в сети (подтверждение, что USDT реально пришли на один
// из наших депозитных адресов). Все вызовы требуют прав админа.

/** Карточка пользователя для раздела «Подписки» */
// GET /api/admin/user/:id
// Ответ: { user, subscription, payments: [...], plans }
export const getAdminUser = (userId) =>
  request(`/admin/user/${userId}`)

/** Продлить подписку пользователя на N дней */
// POST /api/admin/subscriptions/extend
// Тело: { userId, days, kind? }
//   kind: 'real' — выдать дни реального счёта (демо-юзеру — апгрейд с сейчас),
//         'demo' — дни демо-счёта (реальную подписку не понижает).
//   Без kind — продлить текущий тип подписки.
// Ответ: { ok, expiresAt, plan, kind, subscription }
export const adminExtendSubscription = (userId, days, kind) =>
  request('/admin/subscriptions/extend', {
    method: 'POST',
    body: JSON.stringify({ userId, days, ...(kind ? { kind } : {}) }),
  })

/** Полностью аннулировать подписку пользователя */
// POST /api/admin/subscriptions/cancel
// Тело: { userId }
// Ответ: { ok, changed }
export const adminCancelSubscription = (userId) =>
  request('/admin/subscriptions/cancel', {
    method: 'POST',
    body: JSON.stringify({ userId }),
  })

/** Ручная активация тарифа (после проверки оплаты) */
// POST /api/admin/subscriptions/activate
// Тело: { userId, planCode, network?, txHash? }
// Ответ: { ok, subscriptionEnd, expiresAt }
export const adminActivateSubscription = (userId, planCode, extra = {}) =>
  request('/admin/subscriptions/activate', {
    method: 'POST',
    body: JSON.stringify({ userId, planCode, ...extra }),
  })

/** Проверить транзакцию по хэшу в сети (админ-инструмент) */
// POST /api/admin/verify-hash
// Тело: { network, txHash, address? } — address сужает поиск до кошелька-получателя
// Ответ: { ok, found, network, from, to, amount, currency, confirmed, note }
export const adminVerifyHash = (network, txHash, address = '') =>
  request('/admin/verify-hash', {
    method: 'POST',
    body: JSON.stringify({ network, txHash, address }),
  })

/** Рассылка сообщения всем активным пользователям (раздел «Рассылка») */
// POST /api/admin/broadcast
// Тело: { message }
// Ответ: { ok, total, queued } — отправка идёт в фоне на сервере
export const adminBroadcast = (message) =>
  request('/admin/broadcast', {
    method: 'POST',
    body: JSON.stringify({ message }),
  })

/** Сводка по приложению (раздел «Статистика») */
// GET /api/admin/stats
// Ответ: { revenue: {total, month, week, byPlan, byNetwork},
//          users: {total, active, seenToday, seen7d, seen30d, withApiKeys,
//                  withBots, onDemo, onReal},
//          funnel: {registered, viewedApp, everCreatedPayment, everPaid,
//                   notReachedPayment, startedNotPaid, noSubscription} }
export const getAdminStats = () => request('/admin/stats')

// ─── Админка: разделы-кнопки ──────────────────────────────────────────────
//
// Все вызовы ниже требуют прав админа (id в ADMIN_IDS/ADMIN_ID на бэкенде);
// не-админ получает 403. Читающие — GET, меняющие состояние — POST.

const qs = (params) => {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(params || {})) {
    if (v !== undefined && v !== null && v !== '') p.set(k, String(v))
  }
  const s = p.toString()
  return s ? `?${s}` : ''
}

// ── Пользователи ──
/** Список пользователей с фильтром и поиском */
// GET /api/admin/users?q&filter&limit&offset
// filter: all|active|blocked|subscribed|expired|nosub|withbots|withkeys|paid|today
// Ответ: { rows: [{id, username, first_name, bots, botsRunning, paidTotal,
//                  hasKeys, subActive, ...}], total, limit, offset }
export const getAdminUsers = (params) => request(`/admin/users${qs(params)}`)

/** Заблокировать / разблокировать пользователя (блокировка останавливает его ботов) */
// POST /api/admin/users/active — тело { userId, active }
// Ответ: { ok, userId, active, botsStopped }
export const adminSetUserActive = (userId, active) =>
  request('/admin/users/active', { method: 'POST', body: JSON.stringify({ userId, active }) })

// ── Оплаты ──
/** Заявки на оплату (status: all|pending|paid|expired|failed) */
// GET /api/admin/payments?status&limit → { rows, byStatus, limit, status }
export const getAdminPayments = (params) => request(`/admin/payments${qs(params)}`)

/** Подтвердить заявку вручную: помечает paid И активирует подписку */
// POST /api/admin/payments/confirm — тело { id, txHash?, amount? }
export const adminConfirmPayment = (id, txHash, amount) =>
  request('/admin/payments/confirm', { method: 'POST', body: JSON.stringify({ id, txHash, amount }) })

/** Отклонить заявку (pending → failed, адрес возвращается в пул) */
// POST /api/admin/payments/reject — тело { id }
export const adminRejectPayment = (id) =>
  request('/admin/payments/reject', { method: 'POST', body: JSON.stringify({ id }) })

// ── Финансовый журнал ──
/** Журнал оплат с фильтрами (period: all|today|7d|30d|month) */
// GET /api/admin/finance/journal?period&network&plan&status&limit
// Ответ: { rows, totals, byStatus, byNetwork, byPlan, byDay, filters }
export const getFinanceJournal = (params) => request(`/admin/finance/journal${qs(params)}`)

/** Сброс статистики журнала. on=true — сбросить, on=false — вернуть как было.
 *  Неразрушающий: записи оплат не удаляются, только метка времени в app_config. */
// POST /api/admin/finance/reset — тело { on } → { ok, resetAt }
export const resetFinanceStats = (on) =>
  request('/admin/finance/reset', { method: 'POST', body: JSON.stringify({ on: !!on }) })

/** Экспорт журнала в CSV (тот же набор фильтров) */
// GET /api/admin/finance/journal/export — скачивается файл
export async function exportFinanceJournal(params) {
  const initData = window.Telegram?.WebApp?.initData ?? ''
  const base = import.meta.env.VITE_API_URL ?? '/api'
  const res = await fetch(`${base}/admin/finance/journal/export${qs(params)}`, {
    headers: initData ? { 'X-Telegram-Init-Data': initData } : {},
  })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return res.text()
}

// ── Тарифы ──
/** Список тарифов */ // GET /api/admin/plans → { plans }
export const getAdminPlans = () => request('/admin/plans')

/** Создать/изменить тариф */
// POST /api/admin/plans — тело { code, title_ru, type, months, price_usdt, discount }
export const adminSavePlan = (plan) =>
  request('/admin/plans', { method: 'POST', body: JSON.stringify(plan) })

/** Удалить тариф */ // POST /api/admin/plans/delete — тело { code }
export const adminDeletePlan = (code) =>
  request('/admin/plans/delete', { method: 'POST', body: JSON.stringify({ code }) })

// ── Пул депозитных адресов ──
/** Состояние пула (+ список адресов сети, если передан network) */
// GET /api/admin/addresses?network → { pool: [{network, free, inUse, total, pending}], list }
export const getAdminAddresses = (network) => request(`/admin/addresses${qs({ network })}`)

/** Добавить адреса в пул (mode: 'import' — только добавить, 'sync' — привести к списку) */
// POST /api/admin/addresses — тело { network, addresses, mode }
export const adminAddAddresses = (network, addresses, mode = 'import') =>
  request('/admin/addresses', { method: 'POST', body: JSON.stringify({ network, addresses, mode }) })

/** Починить пул: освободить адреса без активной заявки */
// POST /api/admin/addresses/repair
export const adminRepairAddresses = () =>
  request('/admin/addresses/repair', { method: 'POST', body: '{}' })

// ── Боты и торговля ──
/** Боты всех пользователей (status: all|active|stopped|error) */
// GET /api/admin/bots?status&limit → { rows: [{..., alive}], byStatus, total }
export const getAdminBots = (params) => request(`/admin/bots${qs(params)}`)

/** Принудительно остановить бота (без botId — все боты юзера) */
// POST /api/admin/bots/stop — тело { userId, botId? }
export const adminStopBot = (userId, botId) =>
  request('/admin/bots/stop', { method: 'POST', body: JSON.stringify({ userId, botId }) })

/** Торговая статистика: реальные/демо, сегодня/неделя/месяц/всё, топ пар */
// GET /api/admin/trading → { real, demo, topPairs, traders }
export const getAdminTrading = () => request('/admin/trading')

/** PNL демо-ботов юзеров — отдельная область раздела «Боты и торговля» */
// GET /api/admin/demo-trading?limit → { today, week, month, all, users, bots, topPairs, botsByStatus }
export const getAdminDemoTrading = (params) => request(`/admin/demo-trading${qs(params)}`)

/** Рекомендация режима Long/Short по живым ставкам фандинга (п.8 task.txt) */
// GET /api/admin/recommend-mode → { recommendation, reasons, stats, topSymbols, cachedAt, cacheAgeSec }
export const getAdminRecommendMode = () => request('/admin/recommend-mode')

// ── Снимок ботов: «Запомнить все данные» → восстановление после рестарта ──
/** Запомнить запущенные боты и их юзеров (перед остановкой/рестартом) */
// POST /api/admin/bots/snapshot → { ok, count, saved_at }
export const adminSaveBotSnapshot = () =>
  request('/admin/bots/snapshot', { method: 'POST', body: '{}' })

/** Состояние снимка: что запомнено и когда */
// GET /api/admin/bots/snapshot → { ok, saved_at, count, rows }
export const getAdminBotSnapshot = () => request('/admin/bots/snapshot')

/** Запустить все боты из снимка (после рестарта) */
// POST /api/admin/bots/restore → { ok, started, skipped, missing }
export const adminRestoreBots = () =>
  request('/admin/bots/restore', { method: 'POST', body: '{}' })

// ── Лидерборд ──
/** Участники доски: виртуальные и реальные, с разбивкой прибыли по источникам */
// GET /api/admin/leaderboard?q&sort&limit → { rows, overview, minProfit, windowDays }
export const getAdminLeaderboard = (params) => request(`/admin/leaderboard${qs(params)}`)

/** Сделки одного участника — «как рисовалась прибыль» построчно */
// GET /api/admin/leaderboard/trades?id&real&limit → { user, rows, pnl7, pnlAll }
export const getAdminLeaderboardTrades = (params) =>
  request(`/admin/leaderboard/trades${qs(params)}`)

/** Правка участника: депозит, сумма сделки, плечо, ник, активность */
// POST /api/admin/leaderboard/user — тело { id, nickname?, capital?, tradeAmount?, leverage?, isActive? }
export const adminLeaderboardSaveUser = (payload) =>
  request('/admin/leaderboard/user', { method: 'POST', body: JSON.stringify(payload) })

/** Привести НАКОПЛЕННУЮ прибыль участника (за всё время) к заданной сумме */
// POST /api/admin/leaderboard/profit — тело { id, pnl7 } → { before, after, trades }
export const adminLeaderboardSetProfit = (id, pnl7) =>
  request('/admin/leaderboard/profit', { method: 'POST', body: JSON.stringify({ id, pnl7 }) })

/** Добавить нового виртуального участника */ // POST /api/admin/leaderboard/add
export const adminLeaderboardAddUser = () =>
  request('/admin/leaderboard/add', { method: 'POST', body: '{}' })

/** Удалить участника целиком ({ id }) или одну его сделку ({ tradeId }) */
// POST /api/admin/leaderboard/delete
export const adminLeaderboardDelete = (payload) =>
  request('/admin/leaderboard/delete', { method: 'POST', body: JSON.stringify(payload) })

/** Хвост лога начислений лидерборда (первичный след волн) */
// GET /api/admin/leaderboard/log?limit → { file, total, lines }
export const getAdminLeaderboardLog = (params) =>
  request(`/admin/leaderboard/log${qs(params)}`)

// ── Stars (админ) ──
/** Баланс Stars бота: остаток, «созревшие» (21 день), даты разморозки */
// GET /api/admin/stars/balance → { source, balance, eligible, held, ... }
export const getAdminStarsBalance = () => request('/admin/stars/balance')

/** Курс Stars для карточки цены: курс покупки (цена юзера) и наш (сколько приходит) */
// GET /api/admin/stars/rate → { buyerUsdPerStar, netUsdPerStar, mode, plans: [...] }
export const getAdminStarsRate = () => request('/admin/stars/rate')

// ── Рефералы (админ) ──
/** Сводка + детализация реферальной программы (начисления и выплаты) */
// GET /api/admin/referrals → { overview, rows, username }
// overview: { referrersCount, rowsCount, friendsPending, prevPendingCount,
//             accruedAll, pendingAll, paidAll, pendingPrev, accruingThisMonth,
//             dueDate, curMonth, prevMonth, pct, enabled }
// rows:     рефереры с раскрываемыми друзьями (каждая начисленная оплата)
export const getAdminReferrals = () => request('/admin/referrals')

/** Отметить начисления выплаченными (владелец перевёл вручную до 5-го числа) */
// POST /api/admin/referrals/pay — тело { ids: [...], note? } | { period: 'YYYY-MM', note? }
// Ответ: { ok, changes }
export const adminReferralMarkPaid = (payload) =>
  request('/admin/referrals/pay', { method: 'POST', body: JSON.stringify(payload) })

// ── Здоровье и логи ──
/** Здоровье системы: мониторы сетей, воркеры, боты, БД, сервер */
// GET /api/admin/health → { monitors, workersBy, activeWorkers, bots, db, server, freeAddresses }
export const getAdminHealth = () => request('/admin/health')

/** Хвост лога API (only='errors' — только строки с ошибками) */
// GET /api/admin/logs?lines&only → { file, exists, size, lines: [...] }
export const getAdminLogs = (params) => request(`/admin/logs${qs(params)}`)

/** Журнал событий пользователей (аудит) */
// GET /api/admin/audit?limit&userId&event → { rows, kinds, limit }
export const getAdminAudit = (params) => request(`/admin/audit${qs(params)}`)

// ── Поддержка ──
/** Диалоги поддержки (status: open|closed|all) */
// GET /api/admin/support?status&limit → { rows, stats: {open, messagesToday, waitingAnswer} }
export const getAdminSupport = (params) => request(`/admin/support${qs(params)}`)

/** История одного диалога */
// GET /api/admin/support/history?userId → { conversation, messages }
export const getAdminSupportHistory = (userId) =>
  request(`/admin/support/history${qs({ userId })}`)

/** Ответить пользователю из админки (сообщение приходит в чат с ботом) */
// POST /api/admin/support/reply — тело { userId, text } → { ok, delivered }
export const adminSupportReply = (userId, text) =>
  request('/admin/support/reply', { method: 'POST', body: JSON.stringify({ userId, text }) })

/** Закрыть диалог поддержки */
// POST /api/admin/support/close — тело { userId }
export const adminSupportClose = (userId) =>
  request('/admin/support/close', { method: 'POST', body: JSON.stringify({ userId }) })

// ── Бэкапы ──
/** Список бэкапов + расписание */
// GET /api/admin/backups → { dir, keepDays, intervalHours, lastRun, backups }
export const getAdminBackups = () => request('/admin/backups')

/** Сделать бэкап сейчас */ // POST /api/admin/backups/run
export const adminRunBackup = () =>
  request('/admin/backups/run', { method: 'POST', body: '{}' })

// ── Прочее ──
/** Отправить админу тестовое уведомление (проверка chat_id и бота) */
// POST /api/admin/test-notification → { ok, chatId, enabled }
export const adminTestNotification = () =>
  request('/admin/test-notification', { method: 'POST', body: '{}' })

/** Режим юзера (п.1-7 task.txt): включить/выключить имперсонацию тестового юзера */
// POST /api/admin/view-mode — тело { on: boolean }
// on:true  → сбрасывает тестового юзера (новый юзер с чистого листа) → { ok, viewUserId }
// on:false → просто { ok }
export const adminSetViewMode = (on) =>
  request('/admin/view-mode', { method: 'POST', body: JSON.stringify({ on }) })

// ── Рассылка (расширенная) ──
/** Сегменты, их размеры и история рассылок */
// GET /api/admin/broadcast/info → { segments: [...], counts: {seg: n}, history: [...] }
export const getBroadcastInfo = () => request('/admin/broadcast/info')

/** Отправить/запланировать рассылку по сегменту */
// POST /api/admin/broadcast
// Тело: { message, messageEn?, segment?, parseMode?, testOnly?, scheduledAt?, testLang? }
//   messageEn можно не передавать — сервер сам переведёт message (RU→EN) и
//   положит перевод в историю; testLang:'en' — тестовая отправка английским.
// Ответ: { ok, id, total, queued } | { ok, scheduled: true, id, scheduledAt, total }
//        + translation: { translated, provider, warn, error } — что стало с
//        английским текстом (translated:false = англоязычным уйдёт русский).
export const adminSendBroadcast = (payload) =>
  request('/admin/broadcast', { method: 'POST', body: JSON.stringify(payload) })

/** Перевод текста рассылки RU→EN (кнопка «Перевести на английский») */
// POST /api/admin/translate — тело { text, parseMode? }
// Ответ: { ok, text, provider, warn, error }. ok=false — перевод не вышел,
// причина показывается админу; рассылка при этом не блокируется.
export const adminTranslate = (text, parseMode) =>
  request('/admin/translate', { method: 'POST', body: JSON.stringify({ text, parseMode: parseMode || undefined }) })

/** Отменить отложенную рассылку (начатую отменить нельзя) */
// POST /api/admin/broadcast/cancel — тело { id }
export const adminCancelBroadcast = (id) =>
  request('/admin/broadcast/cancel', { method: 'POST', body: JSON.stringify({ id }) })

// ─── Админка: UTM-трекинг ──────────────────────────────────────────────────
//
// Раздел «UTM» в админ-панели: сколько пользователей пришло по каждому
// источнику/кампании, конверсия в подписку, свежие переходы. Метки собирает
// фронт при первом входе юзера (см. services/utm.js → captureUtm).

/** Статистика источников трафика (только админ) */
// GET /api/utm/stats
// Ответ: { total, bySource: [{source, users, subscribers}],
//          byCampaign: [{campaign, users, subscribers}],
//          byEntryMedium: [{source, users, subscribers}],
//          recent: [{user_id, utm_source, utm_medium, utm_campaign, utm_term,
//                    utm_content, entry_medium, first_seen_at, last_seen_at,
//                    username, first_name}] }
export const getUtmStats = (days) =>
  request(`/utm/stats${days ? `?days=${encodeURIComponent(days)}` : ''}`)

/** Динамика переходов по дням (раздел «UTM», график) */
// GET /api/admin/utm/dynamics?days=14
// Ответ: { days, rows: [{day, users, paid}], byMedium: [{medium, users}] }
export const getUtmDynamics = (days = 14) =>
  request(`/admin/utm/dynamics?days=${encodeURIComponent(days)}`)

/** Зафиксировать UTM-метки первого входа (вызывается автоматически при старте) */
// POST /api/utm/register
// Тело: { source, medium, campaign, term, content, entryMedium, referer }
// Ответ: { recorded, firstTouch }
export const registerUtm = (payload) =>
  request('/utm/register', { method: 'POST', body: JSON.stringify(payload) })

// ─── Колесо фортуны (2026-10-19) ───────────────────────────────────────────
// Пользовательская часть: данные колеса, счёт на прокрут, последний выигрыш.
// Оплата идёт через Telegram Stars (WebApp.openInvoice), приз начисляет бот
// в successful_payment — фронт только ждёт результат.

/** Данные колеса + история выигрышей юзера */
// GET /api/fortune
// Ответ: { enabled, introEnabled, historyDays, tiers, prizes, rate, history }
export const getFortune = () => request('/fortune')

/** Счёт на прокрут (createInvoiceLink, XTR) */
// POST /api/fortune/invoice — тело { tier: 'base'|'mid'|'adv' }
// Ответ: { ok, invoiceLink, payload, tier, stars, usd, currency }
export const createFortuneInvoice = (tier) =>
  request('/fortune/invoice', { method: 'POST', body: JSON.stringify({ tier }) })

/** Бесплатный прокрут админа (звёзды не списываются) */
// POST /api/fortune/free-spin — тело { tier: 'base'|'mid'|'adv' }
// Ответ: { ok, free, tier, win: { id, tier, prizeId, kind, days, usd, stars, ... } }
// Доступно только админу: сервер отвечает 403 остальным.
export const createFortuneFreeSpin = (tier) =>
  request('/fortune/free-spin', { method: 'POST', body: JSON.stringify({ tier }) })

/** Последний выигрыш (фронт опрашивает после закрытия окна оплаты) */
// GET /api/fortune/last — { ok, win: { ... } | null }
export const getFortuneLast = () => request('/fortune/last')

/** Колесо начало крутиться — сервер считает тайминги от этого момента. */
// POST /api/fortune/spin-started — тело { winId }
// Нужен, чтобы страховочное сообщение не обогнало колесо, если счёт Telegram
// висел открытым, а мини-апп закрыли во время анимации.
export const markFortuneSpinStarted = (winId) =>
  request('/fortune/spin-started', { method: 'POST', body: JSON.stringify({ winId }) })

/** Колесо остановилось — подтверждаем серверу, что приз показан. */
// POST /api/fortune/spin-shown — тело { winId }
// Только после этого бот присылает уведомление в чат (строго после остановки).
export const markFortuneShown = (winId) =>
  request('/fortune/spin-shown', { method: 'POST', body: JSON.stringify({ winId }) })

// ─── Админка: колесо фортуны ───────────────────────────────────────────────

/** Полная картина колеса: конфиг, статистика, история, очередь выплат */
// GET /api/admin/fortune
export const getAdminFortune = () => request('/admin/fortune')

/** Сохранить настройки/призы колеса (валидация на сервере) */
// POST /api/admin/fortune — тело { fortuneWheel: { ... } }
export const saveAdminFortune = (fortuneWheel) =>
  request('/admin/fortune', { method: 'POST', body: JSON.stringify({ fortuneWheel }) })

/** Вручную разобрать очередь денежных выплат (возврат звёзд) */
// POST /api/admin/fortune/retry
export const retryFortunePayouts = () =>
  request('/admin/fortune/retry', { method: 'POST' })